// src/components/seguros/SeccionPolizasPaciente.js
// =========================================================================
// SEGUROS DEL PACIENTE
// Pólizas del paciente con las aseguradoras de la clínica. Recepción y
// dirección las registran; al dar una cita por seguro se elige una vigente.
// Una póliza no se borra (hay citas que la usan): se da de baja.
// =========================================================================
import React, { useCallback, useEffect, useState } from 'react';
import { format } from 'date-fns';
import api from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import { Modal } from '../common/ui';

const entrada = 'w-full px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm font-semibold text-slate-800 focus:outline-none focus:border-clinica-primary focus:ring-2 focus:ring-clinica-primary/15';
const etiqueta = 'block mb-1 text-xs font-bold text-slate-600';

const ESTILO_ESTADO = {
    vigente: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    caducada: 'bg-amber-50 text-amber-700 border-amber-200',
    sin_concierto: 'bg-slate-100 text-slate-500 border-slate-200',
    inactiva: 'bg-slate-100 text-slate-500 border-slate-200'
};
const TEXTO_ESTADO = { vigente: 'Vigente', caducada: 'Caducada', sin_concierto: 'Sin concierto', inactiva: 'De baja' };

function ModalPoliza({ pacienteId, poliza, aseguradoras, onCerrar, onGuardada }) {
    const editando = Boolean(poliza);
    const [aseguradora, setAseguradora] = useState(poliza?.aseguradora.id || aseguradoras[0]?.id || '');
    const [numeroPoliza, setNumeroPoliza] = useState(poliza?.numeroPoliza || '');
    const [titular, setTitular] = useState(poliza?.titular || '');
    const [esBeneficiario, setEsBeneficiario] = useState(Boolean(poliza?.titular));
    const [fechaCaducidad, setFechaCaducidad] = useState(poliza?.fechaCaducidad ? poliza.fechaCaducidad.slice(0, 10) : '');
    const [error, setError] = useState('');
    const [guardando, setGuardando] = useState(false);

    const guardar = async (e) => {
        e.preventDefault();
        if (!numeroPoliza.trim()) return setError('Indica el número de póliza o de tarjeta.');
        if (esBeneficiario && !titular.trim()) return setError('Indica el nombre del titular de la póliza.');
        setError('');
        setGuardando(true);
        const datos = { numeroPoliza: numeroPoliza.trim(), titular: esBeneficiario ? titular.trim() : '', fechaCaducidad };
        try {
            const res = editando
                ? await api.put(`/seguros/polizas/${poliza.id}`, datos)
                : await api.post('/seguros/polizas', { ...datos, paciente: pacienteId, aseguradora });
            onGuardada(res);
        } catch (err) {
            setError(err.message);
        } finally {
            setGuardando(false);
        }
    };

    return (
        <Modal open onClose={onCerrar} titulo={editando ? `Póliza de ${poliza.aseguradora.nombre}` : 'Añadir seguro'} size="sm">
            <form onSubmit={guardar} noValidate className="space-y-4">
                {!editando && (
                    <div>
                        <label htmlFor="pol-aseg" className={etiqueta}>Aseguradora</label>
                        <select id="pol-aseg" value={aseguradora} onChange={e => setAseguradora(e.target.value)} className={entrada}>
                            {aseguradoras.map(a => <option key={a.id} value={a.id}>{a.nombre}</option>)}
                        </select>
                    </div>
                )}
                <div>
                    <label htmlFor="pol-num" className={etiqueta}>Número de póliza o tarjeta</label>
                    <input id="pol-num" value={numeroPoliza} onChange={e => setNumeroPoliza(e.target.value)} maxLength={40} autoComplete="off" className={entrada} />
                </div>
                <div>
                    <label htmlFor="pol-cad" className={etiqueta}>Caduca el (opcional)</label>
                    <input id="pol-cad" type="date" value={fechaCaducidad} onChange={e => setFechaCaducidad(e.target.value)} className={entrada} />
                </div>
                <label className="flex items-center gap-2 text-sm font-bold text-slate-700">
                    <input type="checkbox" checked={esBeneficiario} onChange={e => setEsBeneficiario(e.target.checked)} className="w-4 h-4 accent-clinica-primary" />
                    El titular es otra persona (el paciente es beneficiario)
                </label>
                {esBeneficiario && (
                    <div>
                        <label htmlFor="pol-tit" className={etiqueta}>Nombre del titular</label>
                        <input id="pol-tit" value={titular} onChange={e => setTitular(e.target.value)} maxLength={120} className={entrada} />
                    </div>
                )}

                {error && <p role="alert" className="p-3 rounded-xl bg-rose-50 border border-rose-200 text-sm font-semibold text-rose-700">{error}</p>}

                <div className="flex justify-end gap-3 pt-2">
                    <button type="button" onClick={onCerrar} className="px-5 py-2.5 rounded-xl text-sm font-bold text-slate-600 hover:bg-slate-100">Cancelar</button>
                    <button type="submit" disabled={guardando}
                            className="px-5 py-2.5 rounded-xl text-sm font-black text-white bg-clinica-primary hover:bg-blue-700 disabled:opacity-50">
                        {guardando ? 'Guardando…' : 'Guardar'}
                    </button>
                </div>
            </form>
        </Modal>
    );
}

export default function SeccionPolizasPaciente({ pacienteId }) {
    const { user } = useAuth();
    const { showToast } = useToast();
    const rol = user?.role || user?.rol;
    const puedeGestionar = ['recepcion', 'supermedico', 'admin'].includes(rol);

    const [polizas, setPolizas] = useState(null);
    const [aseguradoras, setAseguradoras] = useState([]);
    const [error, setError] = useState('');
    const [editando, setEditando] = useState(null); // null | 'nueva' | póliza

    const cargar = useCallback(async () => {
        try {
            const [lista, asegs] = await Promise.all([
                api.get(`/seguros/polizas?paciente=${pacienteId}`),
                api.get('/seguros/aseguradoras')
            ]);
            setPolizas(Array.isArray(lista) ? lista : []);
            setAseguradoras(Array.isArray(asegs) ? asegs : []);
            setError('');
        } catch (err) {
            setError(err.message);
            setPolizas([]);
        }
    }, [pacienteId]);

    useEffect(() => { cargar(); }, [cargar]);

    const cambiarAlta = async (poliza) => {
        try {
            await api.put(`/seguros/polizas/${poliza.id}`, { activa: !poliza.activa });
            showToast(poliza.activa ? 'Póliza dada de baja.' : 'Póliza reactivada.', 'success');
            cargar();
        } catch (err) {
            showToast(err.message, 'error');
        }
    };

    return (
        <div className="bg-white p-6 md:p-8 rounded-3xl border border-slate-200 shadow-sm space-y-6">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-slate-100 pb-5">
                <div>
                    <h3 className="text-xl font-black text-slate-900">Seguros</h3>
                    <p className="text-sm font-medium text-slate-500 mt-1">Al dar cita se puede elegir una póliza vigente: la aseguradora paga según su baremo.</p>
                </div>
                {puedeGestionar && aseguradoras.length > 0 && (
                    <button type="button" onClick={() => setEditando('nueva')}
                            className="px-5 py-2.5 rounded-xl text-sm font-black text-white bg-clinica-primary hover:bg-blue-700 shadow-md">
                        + Añadir seguro
                    </button>
                )}
            </div>

            {error && <p className="text-sm font-semibold text-rose-600">{error}</p>}
            {polizas === null && <div className="h-24 bg-slate-50 rounded-2xl animate-pulse" />}
            {polizas?.length === 0 && !error && (
                <p className="text-sm font-semibold text-slate-400 text-center py-6">
                    Este paciente no tiene seguros registrados.
                    {puedeGestionar && aseguradoras.length === 0 ? ' Primero hay que dar de alta las aseguradoras en «Aseguradoras».' : ''}
                </p>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {polizas?.map(p => (
                    <div key={p.id} className={`p-5 rounded-2xl border border-slate-200 space-y-2 ${p.estado === 'vigente' ? '' : 'opacity-70'}`}>
                        <div className="flex items-start justify-between gap-2">
                            <p className="text-sm font-black text-slate-900">{p.aseguradora.nombre}</p>
                            <span className={`shrink-0 px-2 py-0.5 rounded-md border text-[10px] font-black uppercase tracking-widest ${ESTILO_ESTADO[p.estado]}`}>
                                {TEXTO_ESTADO[p.estado]}
                            </span>
                        </div>
                        <p className="text-sm font-mono font-bold text-slate-700">{p.numeroPoliza}</p>
                        <p className="text-[11px] font-semibold text-slate-400">
                            {p.titular ? `Beneficiario · titular: ${p.titular}` : 'Titular'}
                            {p.fechaCaducidad ? ` · caduca el ${format(new Date(p.fechaCaducidad), 'dd/MM/yyyy')}` : ''}
                        </p>
                        {puedeGestionar && (
                            <div className="flex gap-4 pt-1">
                                <button type="button" onClick={() => setEditando(p)} className="text-xs font-black text-clinica-primary hover:underline">Editar</button>
                                <button type="button" onClick={() => cambiarAlta(p)} className={`text-xs font-black hover:underline ${p.activa ? 'text-rose-600' : 'text-emerald-700'}`}>
                                    {p.activa ? 'Dar de baja' : 'Reactivar'}
                                </button>
                            </div>
                        )}
                    </div>
                ))}
            </div>

            {editando && (
                <ModalPoliza
                    pacienteId={pacienteId}
                    poliza={editando === 'nueva' ? null : editando}
                    aseguradoras={aseguradoras}
                    onCerrar={() => setEditando(null)}
                    onGuardada={() => { setEditando(null); showToast('Seguro guardado.', 'success'); cargar(); }}
                />
            )}
        </div>
    );
}
